import { useRouter } from 'expo-router';

import { primum_default_avatar } from '@/assets/images';
import { Avatar, AvatarImage } from '@/components/ui/avatar';
import { Box } from '@/components/ui/box';
import { Pressable } from '@/components/ui/pressable';
import { NAV_LABELS } from '@/constants/navigation';
import { ROUTES } from '@/constants/routes';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { useHaptic } from '@/hooks/useHaptic';
import { useResumableSurveys } from '@/features/surveys';
import { useUpdateBanner } from '@/hooks/useUpdateBanner';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { getAccessibleHitSlop } from '@/util/accessibleTouchTarget';
import { getAccountButtonAccessibilityLabel } from '@/util/accountBadgeLabels';
import { trackAccountHeaderAvatarClicked } from '@/util/analyticsEvents';
import { getAvatarUrl } from '@/util/getAvatarUrl';

const AVATAR_SIZE = 36;

/**
 * The reader's avatar in a tab header, opening Account as a pushed screen.
 *
 * Wears a dot for a pending survey or a ready update, and the spoken label names both,
 * since one dot stands for either. Render it from `headerRight` on a tab screen.
 */
export function AccountHeaderButton() {
  const router = useRouter();

  const haptic = useHaptic();

  const { currentUser, needsNpiVerification } = useAuthStatus();

  const { badgeCount: resumableSurveyCount } = useResumableSurveys();

  const { showUpdateBadge } = useUpdateBanner();

  if (needsNpiVerification) {
    return null;
  }

  const showBadge = resumableSurveyCount > 0 || showUpdateBadge;

  const accessibilityLabel = getAccountButtonAccessibilityLabel({
    baseLabel: NAV_LABELS.account.mobileTab,
    resumableSurveyCount,
    showUpdateBadge,
  });

  const accessibilityProps = getAccessibilityProps({
    label: accessibilityLabel,
    role: 'button',
    hint: 'Opens your account',
  });

  return (
    <Pressable
      {...accessibilityProps}
      hitSlop={getAccessibleHitSlop(AVATAR_SIZE)}
      onPress={() => {
        haptic.impactLight();

        trackAccountHeaderAvatarClicked();

        router.push(ROUTES.ACCOUNT);
      }}>
      <Box className="relative">
        <Avatar size="sm" className="h-9 w-9">
          <AvatarImage source={getAvatarUrl(currentUser?.avatar) || primum_default_avatar} />
        </Avatar>

        {showBadge ? (
          <Box className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-background-0 bg-tertiary-400" />
        ) : null}
      </Box>
    </Pressable>
  );
}
